import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { Building2, CheckCircle2, ChevronLeft, ChevronRight, ArrowLeft } from 'lucide-react';
import { ClientCollaboration } from '../../data/clientsJourneyData';

interface ClientsShowcaseCarouselProps {
    clients: ClientCollaboration[];
    onBack?: () => void;
}

export const ClientsShowcaseCarousel = ({ clients, onBack }: ClientsShowcaseCarouselProps) => {
    // Ensure we have enough items for the effect (min 5 for full center + 2 sides)
    const displayClients = clients.length < 5 ? [...clients, ...clients, ...clients].slice(0, 7) : clients;

    const [currentIndex, setCurrentIndex] = useState(0);
    const [isMobile, setIsMobile] = useState(false);

    useEffect(() => {
        const checkMobile = () => setIsMobile(window.innerWidth < 768);
        checkMobile();
        window.addEventListener('resize', checkMobile);
        return () => window.removeEventListener('resize', checkMobile);
    }, []);

    const nextSlide = () => {
        setCurrentIndex((prev) => (prev + 1) % displayClients.length);
    };

    const prevSlide = () => {
        setCurrentIndex((prev) => (prev - 1 + displayClients.length) % displayClients.length);
    };

    // Auto-play
    useEffect(() => {
        const timer = setInterval(nextSlide, 3000);
        return () => clearInterval(timer);
    }, [displayClients.length]);

    const activeClient = displayClients[currentIndex];
    const activeTheme = activeClient?.theme ?? {
        gradient: "linear-gradient(135deg, rgba(37,99,235,0.18) 0%, rgba(255,255,255,0.8) 50%, rgba(245,158,11,0.2) 100%)",
        glow: "rgba(37,99,235,0.3)",
        accent: "#2563eb"
    };
    const activeBackgroundImage = activeClient?.backgroundImage;

    const getCardStyle = (index: number) => {
        const total = displayClients.length;

        // Calculate shortest distance in a circular array
        let diff = (index - currentIndex) % total;
        if (diff < -total / 2) diff += total;
        if (diff > total / 2) diff -= total;

        const absDiff = Math.abs(diff);
        const style: any = {
            x: 0,
            scale: 1,
            opacity: 0,
            zIndex: 0,
            rotateY: 0,
            blur: 0,
            display: 'block'
        };

        if (isMobile) {
            // Mobile: Stacked plain slider style or simple fade
            if (diff === 0) {
                style.opacity = 1;
                style.zIndex = 10;
                style.scale = 1;
            } else if (absDiff === 1) {
                style.opacity = 0.5;
                style.scale = 0.9;
                style.x = diff > 0 ? 50 : -50; // overlap slightly
                style.zIndex = 5;
            } else {
                style.display = 'none';
            }
            return style;
        }

        // Ivory Reference Gallery - Balanced Impact Dimensions
        if (diff === 0) {
            // Main Dominant Hero - Large and on the Right
            style.zIndex = 100;
            style.opacity = 1;
            style.scale = 1.3; // Increased for better impact
            style.x = isMobile ? '0%' : '42%';
            style.y = isMobile ? 0 : -30;
            style.blur = 0;
        } else if (diff > 0) {
            // Upcoming Queue - Substantial Side-by-Side Stagger
            const qIdx = diff;
            style.zIndex = 80 - qIdx;
            style.opacity = 1;
            style.scale = 0.95 - (qIdx * 0.18);

            // Balanced X spacing
            style.x = isMobile ? '0%' : `${-28 * qIdx - 10}%`;

            // Inclined floor logic
            style.y = isMobile ? 0 : (qIdx * 80) + 40;

            style.blur = qIdx * 0.5;
            style.display = qIdx > 4 ? 'none' : 'block';
        } else {
            // Past items
            style.zIndex = 10;
            style.opacity = 0;
            style.x = '130%';
            style.y = 0;
        }

        return style;
    };

    return (
        <>
            <section className="relative py-8 md:py-14 px-4 overflow-hidden bg-white">

                {/* Dynamic Project Backdrop */}
                <div className="absolute inset-0 pointer-events-none" style={{ backgroundImage: activeTheme.gradient }} />
                <motion.div
                    className="absolute -top-24 -right-16 w-[65vw] h-[65vw] rounded-full blur-[140px] pointer-events-none"
                    style={{ backgroundColor: activeTheme.glow }}
                    animate={{ x: [0, -30, 0], y: [0, 20, 0] }}
                    transition={{ duration: 16, repeat: Infinity, ease: "easeInOut" }}
                />
                
                <motion.div
                    className="absolute -bottom-24 -left-16 w-[65vw] h-[65vw] rounded-full blur-[140px] pointer-events-none"
                    style={{ backgroundColor: activeTheme.glow }}
                    animate={{ x: [0, 25, 0], y: [0, -20, 0] }}
                    transition={{ duration: 18, repeat: Infinity, ease: "easeInOut" }}
                />
                <div className="absolute inset-0 pointer-events-none opacity-40" style={{ backgroundImage: "radial-gradient(circle at 1px 1px, rgba(255,255,255,0.6) 1px, transparent 0)", backgroundSize: "22px 22px" }} />
                <motion.button
                            initial={{ opacity: 0, x: -20 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ duration: 0.5 }}
                            onClick={onBack || (() => window.history.back())}
                            className="flex items-center gap-3 px-5 py-2.5 bg-white/50 hover:bg-white/80 backdrop-blur-md border mt-4 border-white/50 rounded-full text-slate-800 text-xs font-semibold transition-all duration-300 group hover:shadow-lg hover:-translate-y-0.5 w-fit"
                        >
                            <ArrowLeft className="w-5 h-5 group-hover:-translate-x-1 transition-transform duration-300" />
                            <span>Back to Anuvadini Home Page</span>
                        </motion.button>

                <div className="max-w-7xl mx-auto relative z-10">
                    <div className="flex flex-col gap-8">
                        

                        {/* Project Overview Header */}
                        <AnimatePresence mode="wait">
                            {activeClient && (
                                <motion.div
                                    key={activeClient.id}
                                    initial={{ opacity: 0, y: 12 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    exit={{ opacity: 0, y: -12 }}
                                    transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
                                    className="grid gap-6 lg:grid-cols-[1.05fr_0.95fr] items-end"
                                >
                                    <div className="space-y-5 order-1">
                                        <div className="flex flex-wrap items-center gap-3">
                                            <span
                                                className="text-[10px] font-black uppercase tracking-[0.35em] px-4 py-2 rounded-full border"
                                                style={{ color: activeTheme.accent, borderColor: `${activeTheme.accent}55`, backgroundColor: `${activeTheme.accent}1A` }}
                                            >
                                                {activeClient.organizationType}
                                            </span>
                                            <span className="text-[10px] font-black uppercase tracking-[0.35em] text-slate-500">
                                                {activeClient.projectNature}
                                            </span>
                                        </div>
                                        <h2
                                            className="text-3xl md:text-5xl font-black tracking-tight text-slate-900"
                                            style={{ fontFamily: 'var(--font-display)' }}
                                        >
                                            {activeClient.name}
                                        </h2>
                                        <p className="text-sm md:text-base text-slate-700/90 leading-relaxed max-w-2xl">
                                            {activeClient.summary}
                                        </p>
                                        <div className="flex flex-wrap gap-2">
                                            {activeClient.highlights.map((highlight, idx) => (
                                                <span
                                                    key={idx}
                                                    className="text-[10px] font-bold uppercase tracking-widest rounded-full px-3 py-1.5 border"
                                                    style={{ color: activeTheme.accent, borderColor: `${activeTheme.accent}55`, backgroundColor: `${activeTheme.accent}12` }}
                                                >
                                                    {highlight}
                                                </span>
                                            ))}
                                        </div>
                                    </div>
                                    <div className="order-2 grid gap-5">
                                        <div className="relative overflow-hidden rounded-[32px] border border-white/60 bg-white/70 backdrop-blur-xl shadow-[0_30px_80px_rgba(15,23,42,0.2)]">
                                            <div
                                                className="h-[220px] md:h-[280px] w-full bg-center bg-cover"
                                                style={{ backgroundImage: activeBackgroundImage ? `url(${activeBackgroundImage})` : activeTheme.gradient }}
                                            />
                                            <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-slate-950/35" />
                                            <div className="absolute bottom-5 left-6 right-6 flex items-center justify-between">
                                                <span className="text-[11px] font-black uppercase tracking-[0.3em] text-white/90">Project Preview</span>
                                                <span className="text-[11px] font-black uppercase tracking-[0.3em] text-white/70">{activeClient.projectNature}</span>
                                            </div>
                                        </div>
                                        <div className="relative rounded-[24px] border border-white/60 bg-white/70 backdrop-blur-xl p-5 md:p-6 shadow-[0_30px_80px_rgba(15,23,42,0.16)]">
                                            <div className="text-xs font-black uppercase tracking-[0.3em] text-slate-500">Impact</div>
                                            <p className="mt-3 text-sm text-slate-700 leading-relaxed">
                                                {activeClient.impact}
                                            </p>
                                            <div className="mt-4 flex items-center gap-3 text-xs uppercase tracking-[0.25em] text-slate-500">
                                                <span>Focus</span>
                                                <span className="text-slate-700 font-semibold">{activeClient.collaborationFocus}</span>
                                            </div>
                                        </div>
                                    </div>
                                </motion.div>
                            )}
                        </AnimatePresence>
                    </div>

                    {/* Carousel Stage - increased height for 3D effect */}
                    <div className="relative mt-8 h-[420px] md:h-[520px] flex items-center justify-center perspective-1000">
                        {/* Navigation Buttons - Elegant Minimalist */}
                        <div className="absolute bottom-10 left-4 md:left-24 z-50 flex gap-4">
                            <button
                                onClick={prevSlide}
                                className="p-3 rounded-full bg-white/10 backdrop-blur-md border border-white/20 text-white hover:bg-white hover:text-slate-900 transition-all duration-300 shadow-xl"
                            >
                                <ChevronLeft className="w-6 h-6" />
                            </button>
                            <button
                                onClick={nextSlide}
                                className="p-3 rounded-full bg-white/10 backdrop-blur-md border border-white/20 text-white hover:bg-white hover:text-slate-900 transition-all duration-300 shadow-xl"
                            >
                                <ChevronRight className="w-6 h-6" />
                            </button>
                        </div>

                        {/* Cards Container */}
                        <div className="relative w-full max-w-6xl h-full flex items-center justify-center perspective-1000">
                            <AnimatePresence initial={false}>
                                {displayClients.map((client, index) => {
                                    const style = getCardStyle(index);
                                    if (style.display === 'none') return null;

                                    return (
                                        <motion.div
                                            key={index}
                                            className="absolute w-[210px] md:w-[360px] cursor-pointer"
                                            initial={false}
                                            animate={{
                                                x: style.x,
                                                y: style.y,
                                                scale: style.scale,
                                                opacity: style.opacity,
                                                zIndex: style.zIndex,
                                                filter: `blur(${style.blur}px)`,
                                            }}
                                            transition={{ duration: 1.5, ease: [0.19, 1, 0.22, 1] }}
                                            onClick={() => setCurrentIndex(index)}
                                        >
                                            <ShowcaseCard client={client} isActive={index === currentIndex} cardIndex={index} />
                                        </motion.div>
                                    );
                                })}
                            </AnimatePresence>
                        </div>

                        <div className="absolute bottom-12 left-4 md:left-64 flex justify-center gap-3 z-50">
                            {displayClients.map((_, idx) => (
                                <button
                                    key={idx}
                                    onClick={() => setCurrentIndex(idx)}
                                    className={`transition-all duration-500 rounded-full h-1.5 ${idx === currentIndex
                                        ? "w-12 bg-white"
                                        : "w-2 bg-white/20 hover:bg-white/40"
                                        }`}
                                />
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* Project Introductions Section */}
            <div className="py-20 md:py-32 bg-gradient-to-br from-indigo-950 via-purple-900 to-pink-900 relative overflow-hidden text-white">
                {/* Enhanced Floating Branded Nodes */}
                <motion.div
                    animate={{ x: [0, 100, 0], y: [0, 50, 0] }}
                    transition={{ duration: 20, repeat: Infinity, ease: "easeInOut" }}
                    className="absolute top-0 right-0 w-[600px] h-[600px] bg-blue-500/10 rounded-full blur-[120px] pointer-events-none"
                />
                <motion.div
                    animate={{ x: [0, -80, 0], y: [0, -100, 0] }}
                    transition={{ duration: 18, repeat: Infinity, ease: "easeInOut" }}
                    className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-purple-500/10 rounded-full blur-[130px] pointer-events-none"
                />

                <div className="container mx-auto px-4 relative z-10">
                    <div className="text-center mb-20">
                        <h2 className="text-4xl md:text-5xl font-black mb-6 text-white tracking-tight drop-shadow-lg">
                            Detailed Project Overviews
                        </h2>
                        <div className="w-32 h-1.5 bg-gradient-to-r from-blue-400 via-purple-400 to-orange-400 mx-auto rounded-full" />
                    </div>

                    <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-10">
                        {clients.map((client: ClientCollaboration, idx: number) => (
                            <motion.div
                                key={client.id}
                                initial={{ opacity: 0, y: 30 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true, margin: "-50px" }}
                                transition={{ duration: 0.8, delay: idx * 0.1 }}
                                className="glass-container rounded-[32px] p-8 md:p-10 relative flex flex-col gap-6 hover:bg-white/10 transition-colors duration-500 group"
                            >
                                <div className="flex items-center gap-6">
                                    <div className="flex-shrink-0">
                                        <div className="w-12 h-12 rounded-2xl flex items-center justify-center text-white font-black text-xl shadow-xl glass-container border-white/20">
                                            {idx + 1}
                                        </div>
                                    </div>
                                    <h3 className="text-2xl md:text-3xl font-black text-white leading-tight drop-shadow-md">
                                        {client.name}
                                    </h3>
                                </div>

                                <div className="space-y-4 flex-1">
                                    <div className="flex">
                                        <span className="text-[10px] font-black uppercase tracking-[0.2em] text-white bg-white/10 px-3 py-1.5 rounded-full border border-white/20 shadow-sm backdrop-blur-md">
                                            {client.organizationType}
                                        </span>
                                    </div>
                                    <p className="text-base md:text-lg text-white/80 leading-relaxed font-normal">
                                        {client.summary}. <span className="text-white font-bold">{client.impact}</span>
                                    </p>
                                </div>

                                <div className="flex flex-wrap items-center gap-6 pt-6 border-t border-white/10 mt-auto">
                                    <div className="flex items-center gap-2.5">
                                        <div className="w-2 h-2 rounded-full bg-white shadow-[0_0_8px_rgba(255,255,255,0.8)]" />
                                        <span className="text-[11px] font-black uppercase tracking-widest text-white/60">{client.projectNature}</span>
                                    </div>
                                    <div className="h-3 w-px bg-white/20 hidden sm:block" />
                                    <div className="flex items-center gap-2">
                                        <span className="text-[11px] font-black uppercase tracking-widest text-white/50">Focus:</span>
                                        <span className="text-[11px] font-black uppercase tracking-widest text-white/80">{client.collaborationFocus}</span>
                                    </div>
                                </div>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </div>
        </>
    );
};

// Card Component
const ShowcaseCard = ({ client, isActive, cardIndex }: { client: ClientCollaboration; isActive: boolean; cardIndex: number }) => {
    const fallbackColors = ['#4f46e5', '#0ea5e9', '#a855f7'];
    const accent = client.theme?.accent ?? fallbackColors[cardIndex % fallbackColors.length];
    const cardGradient = `linear-gradient(160deg, ${accent} 0%, rgba(15,23,42,0.96) 70%)`;

    return (
        <div
            className={`
            aspect-[3/5.1] p-6 md:p-8 relative overflow-hidden flex flex-col items-start text-left
            transition-all duration-700 ease-out group border border-white/20
            ${isActive ? 'shadow-[0_45px_120px_-25px_rgba(15,23,42,0.5)]' : 'shadow-lg grayscale-[8%]'}
        `}
            style={{
                backgroundImage: cardGradient,
                clipPath: 'polygon(0 0, 86% 0, 100% 12%, 100% 100%, 12% 100%, 0 86%)',
                borderRadius: '28px'
            }}
        >
            <div className="absolute -inset-10 opacity-40 blur-2xl pointer-events-none" style={{ background: `radial-gradient(circle at 20% 20%, ${accent}55 0%, transparent 55%)` }} />
            <div className="absolute inset-0 opacity-10 pointer-events-none" style={{ backgroundImage: 'linear-gradient(120deg, rgba(255,255,255,0.4) 0%, transparent 40%), radial-gradient(circle at 2px 2px, rgba(255,255,255,0.6) 1px, transparent 0)', backgroundSize: '100% 100%, 20px 20px' }} />

            <div className="relative z-10 flex items-center gap-3">
                <div className="w-12 h-12 md:w-14 md:h-14 rounded-2xl bg-white/90 flex items-center justify-center shadow-lg">
                    <Building2 className="w-6 h-6 md:w-7 md:h-7" style={{ color: accent }} />
                </div>
                <div className="text-[10px] font-black uppercase tracking-[0.3em] text-white/70">
                    {client.organizationType}
                </div>
            </div>

            <div className="relative z-10 mt-6 space-y-3">
                <h3 className="text-2xl md:text-3xl font-black leading-tight text-white" style={{ fontFamily: 'var(--font-display)' }}>
                    {client.name}
                </h3>
                <p className={`text-xs md:text-sm leading-relaxed text-white/80 ${isActive ? '' : 'line-clamp-2'}`}>
                    {client.summary}
                </p>
            </div>

            <div className="relative z-10 mt-4 w-full">
                <div className="text-[10px] font-bold uppercase tracking-widest text-white/60 mb-2">Key Highlights</div>
                <div className="flex flex-wrap gap-2">
                    {client.highlights.slice(0, 3).map((highlight, idx) => (
                        <span key={idx} className="text-[9px] font-bold bg-white/10 border border-white/20 rounded-full px-3 py-1 text-white">
                            {highlight}
                        </span>
                    ))}
                </div>
            </div>

            <div className={`relative z-10 mt-5 w-full p-4 rounded-2xl bg-white/10 border border-white/10 backdrop-blur-sm transition-all duration-500 ${isActive ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
                <div className="text-[10px] font-black uppercase tracking-[0.3em] text-white/50 mb-1">Project Impact</div>
                <p className="text-xs font-medium text-white leading-relaxed">
                    {client.impact}
                </p>
            </div>

            <div className="relative z-10 w-full mt-auto pt-4 border-t border-white/10 flex justify-between items-center text-[10px] font-bold uppercase tracking-wider text-white/80">
                <span className="flex items-center gap-1.5">
                    <span className={`w-2 h-2 rounded-full ${client.projectNature === 'Ongoing' ? 'bg-green-400' : 'bg-blue-300'} ${isActive ? 'animate-pulse' : ''}`} />
                    {client.projectNature}
                </span>
                <CheckCircle2 className="w-4 h-4 text-white/60 group-hover:text-white transition-colors" />
            </div>
        </div>
    );
};
